import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { RootState } from "app/store";

// The receipt modal renders either a refund (RFD) or an invoice-payment (RCP) receipt;
// `source` picks which receipt endpoint the modal calls. (SN-18363 + SN-18451)
export type ReceiptModalSource = "refund" | "payment";

interface ReceiptModalState {
  open: boolean;
  source: ReceiptModalSource;
  sourceId: number | null;
  patientId: number | null;
}

const initialState: ReceiptModalState = {
  open: false,
  source: "refund",
  sourceId: null,
  patientId: null
};

// One mounted modal, many triggers (refunds/payments table row actions + the post-refund /
// post-payment snackbars). Every trigger dispatches openReceiptModal; the modal reads this
// slice and fetches the matching receipt. See tech-plan D9.
export const refundReceiptModalSlice = createSlice({
  name: "refundReceiptModal",
  initialState,
  reducers: {
    openReceiptModal: (
      state,
      action: PayloadAction<{
        source: ReceiptModalSource;
        sourceId: number;
        patientId: number | null;
      }>
    ) => {
      state.open = true;
      state.source = action.payload.source;
      state.sourceId = action.payload.sourceId;
      state.patientId = action.payload.patientId;
    },
    closeReceiptModal: (state) => {
      state.open = false;
      state.sourceId = null;
      state.patientId = null;
    }
  }
});

export const { openReceiptModal, closeReceiptModal } =
  refundReceiptModalSlice.actions;

// Back-compat refund helpers — existing refund triggers keep calling these.
export const openRefundReceiptModal = (payload: {
  refundId: number;
  patientId: number | null;
}) =>
  openReceiptModal({
    source: "refund",
    sourceId: payload.refundId,
    patientId: payload.patientId
  });
export const closeRefundReceiptModal = closeReceiptModal;

export const selectRefundReceiptModal = (state: RootState) =>
  state.refundReceiptModal;

export default refundReceiptModalSlice.reducer;
